// Web seamless-SSO state that must survive the Auth0 full-page redirect (which destroys
// the JS heap and Redux). Backed by sessionStorage. The native variant keeps the same
// API but stores in memory, since native auth uses an in-app browser and never unloads.
import {
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  SESSION_STORAGE_KEYS,
} from './SessionStorage'

// origin distinguishes a seamless-SSO stash (emits the _started/_success funnel) from a plain
// action/login stash (does not), so the success metric only counts flows that also started.
export type SeamlessDestinationOrigin = 'seamless' | 'action_login'
export type SeamlessDestination = {
  path: string
  props: Record<string, unknown>
  origin: SeamlessDestinationOrigin
}

export const stashDestination = (
  path: string,
  props?: Record<string, unknown>,
  origin: SeamlessDestinationOrigin = 'action_login'
): void => {
  setSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION, path)
  setSessionItem(
    SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION_PROPS,
    JSON.stringify(props || {})
  )
  setSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_ORIGIN, origin)
}

export const consumeDestination = (): SeamlessDestination | null => {
  const path = getSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION)
  if (!path) return null
  const rawProps = getSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION_PROPS)
  const origin = getSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_ORIGIN)
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION)
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION_PROPS)
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_ORIGIN)
  let props: Record<string, unknown> = {}
  try {
    props = JSON.parse(rawProps ?? '{}')
  } catch (_e) {
    // Malformed stash — navigate without props rather than failing startup.
  }
  return { path, props, origin: origin === 'seamless' ? 'seamless' : 'action_login' }
}

// Read the pending stash origin without consuming it, so the success funnel can be
// emitted at login time regardless of which post-login navigation path later consumes
// the destination. Returns null when no destination is stashed.
export const peekDestinationOrigin = (): SeamlessDestinationOrigin | null => {
  if (!getSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION)) return null
  return getSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_ORIGIN) === 'seamless'
    ? 'seamless'
    : 'action_login'
}

export const getPendingManualConnection = (): string | null =>
  getSessionItem(SESSION_STORAGE_KEYS.PENDING_MANUAL_CONNECTION)

export const setPendingManualConnection = (connection: string): void =>
  setSessionItem(SESSION_STORAGE_KEYS.PENDING_MANUAL_CONNECTION, connection)

export const clearPendingManualConnection = (): void =>
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_MANUAL_CONNECTION)

export const clearAll = (): void => {
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_MANUAL_CONNECTION)
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION)
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_SECOND_ACTION_PROPS)
  removeSessionItem(SESSION_STORAGE_KEYS.PENDING_DEEPLINK_ORIGIN)
}
